import useReports from '../../../hooks/data/useReports';
import useAuthSession from '../../../lib/auth/hooks/useAuthSession';
import ActionCount from '../../Action/ActionCount/ActionCount';
import ActiveLink from '../../common/ActiveLink';
import Button from '../../common/Buttons/Button/Button';
import Icon from '../../common/Icon/Icon';
import TakeTheTourModal, { type Tours } from '../../TakeTheTourModal/TakeTheTourModal';

import styles from './TopNavigation.module.css';

interface Props {
    tour?: keyof typeof Tours;
}

const TopNavigation = ({ tour }: Props) => {
    const { session } = useAuthSession({ required: true });
    const { singleSiteId } = session?.user ?? { singleSiteId: null };
    // Report access is allocated per user in auth and most users have none, so the
    // entry is hidden rather than leading to an empty page. Shares the "reports" SWR
    // cache key with the page itself, so this costs no extra request.
    const { hasReports } = useReports();
    return (
        <nav className="hidden min-[1130px]:block bg-white">
            <div className="max-w-app-container mx-auto flex justify-between items-center px-1 xl:px-1.5">
                <ul className={`inline-flex items-center gap-0.75 type-small-caps  pt-1 ${styles.navList}`}>
                    <li>
                        <ActiveLink href="/" className={`${styles.navLink}`} activeClassName={styles.navLinkActive}>
                            <Icon className={styles.navLinkIcon} icon="grid" />
                            Dashboard
                        </ActiveLink>
                    </li>
                    <li>
                        {singleSiteId ? (
                            <ActiveLink
                                href={`/sites/${singleSiteId}`}
                                className={styles.navLink}
                                activeClassName={styles.navLinkActive}
                                partiallyActive
                            >
                                <Icon className={styles.navLinkIcon} icon="pin" />
                                Site
                            </ActiveLink>
                        ) : (
                            <ActiveLink
                                href="/sites"
                                className={styles.navLink}
                                activeClassName={styles.navLinkActive}
                                partiallyActive
                            >
                                <Icon className={styles.navLinkIcon} icon="pin" />
                                Sites
                            </ActiveLink>
                        )}
                    </li>
                    <li>
                        <ActiveLink href="/actions" className={styles.navLink} activeClassName={styles.navLinkActive}>
                            <Icon className={styles.navLinkIcon} icon="sun" />
                            Actions
                            <ActionCount />
                        </ActiveLink>
                    </li>
                    {hasReports ? (
                        <li>
                            <ActiveLink
                                href="/reports"
                                className={styles.navLink}
                                activeClassName={styles.navLinkActive}
                            >
                                <Icon className={styles.navLinkIcon} icon="line_chart" />
                                Reports
                            </ActiveLink>
                        </li>
                    ) : null}
                    <li>
                        <ActiveLink
                            href="/health-and-safety"
                            className={styles.navLink}
                            activeClassName={styles.navLinkActive}
                        >
                            <Icon className={styles.navLinkIcon} icon="folder_open" />
                            Health &amp; Safety
                        </ActiveLink>
                    </li>
                    <li>
                        <ActiveLink href="/contacts" className={styles.navLink} activeClassName={styles.navLinkActive}>
                            <Icon className={styles.navLinkIcon} icon="id_card" />
                            Contacts
                        </ActiveLink>
                    </li>
                </ul>
                {tour ? (
                    <TakeTheTourModal tour={tour}>
                        {({ openModal }) => (
                            <Button className="type-small-caps" variant="secondary" icon="map" onClick={openModal}>
                                Take the tour
                            </Button>
                        )}
                    </TakeTheTourModal>
                ) : null}
            </div>
        </nav>
    );
};

export default TopNavigation;
